// Universal .ics calendar event generator — works with Google, Apple, Outlook.
// All callback events default to 30 minutes.

const pad = (n) => String(n).padStart(2, "0");

const stampUTC = () => {
  const d = new Date();
  return (
    d.getUTCFullYear() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
};

// Convert YYYY-MM-DD + HH:MM into YYYYMMDDTHHMMSS (floating/local time within TZID).
const localStamp = (dateISO, timeHHMM) => {
  const d = (dateISO || "").replaceAll("-", "");
  const t = (timeHHMM || "09:00").replaceAll(":", "") + "00";
  return `${d}T${t}`;
};

// Add minutes to a HH:MM string, rolling over hours if needed.
const addMinutes = (timeHHMM, mins) => {
  const [h, m] = (timeHHMM || "09:00").split(":").map((x) => parseInt(x, 10) || 0);
  const total = h * 60 + m + mins;
  const nh = Math.floor((total % (24 * 60)) / 60);
  const nm = total % 60;
  return `${pad(nh)}:${pad(nm)}`;
};

const esc = (s) =>
  String(s || "")
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");

/**
 * Build a universal .ics string for a callback event.
 * @param {Object} opts
 * @param {string} opts.date   YYYY-MM-DD
 * @param {string} opts.time   HH:MM (24h). Defaults to 09:00.
 * @param {string} [opts.timezone] IANA timezone e.g. "Europe/London". If missing, floating/local time.
 * @param {string} [opts.title]
 * @param {string} [opts.description]
 * @param {string} [opts.location]
 * @param {number} [opts.durationMins] Default 30.
 * @param {string} [opts.uid]
 */
export function buildIcs({ date, time, timezone, title, description, location, durationMins = 30, uid }) {
  if (!date) return null;
  const startLocal = localStamp(date, time || "09:00");
  const endLocal = localStamp(date, addMinutes(time || "09:00", durationMins));
  const tzPart = timezone ? `;TZID=${timezone}` : "";
  const unique = uid || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}@xpressprompts`;

  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//XpressPrompts//Callback//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${unique}`,
    `DTSTAMP:${stampUTC()}`,
    `DTSTART${tzPart}:${startLocal}`,
    `DTEND${tzPart}:${endLocal}`,
    `SUMMARY:${esc(title || "Callback")}`,
    description ? `DESCRIPTION:${esc(description)}` : "",
    location ? `LOCATION:${esc(location)}` : "",
    "BEGIN:VALARM",
    "ACTION:DISPLAY",
    "DESCRIPTION:Reminder",
    "TRIGGER:-PT15M",
    "END:VALARM",
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean);

  return lines.join("\r\n");
}

/** Download an .ics string as a file. */
export function downloadIcs(icsText, filename = "callback.ics") {
  if (!icsText) return;
  const blob = new Blob([icsText], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
